import { styled } from '@mui/material';
import { typography } from '@mui/system';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Hotkey } from 'config/enums';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { deafNameSelector } from 'features/call/call-deaf/store';
import { Separator } from 'features/call/call-details/styles';
import { SipSessionEventBus } from 'features/sip/services';
import { styledProps } from 'features/theme/styled';
import theme from 'features/theme/theme';
import { Button } from 'features/ui/elements';
import { agentNumberSelector } from 'features/user/store';
import { useWhileComponentMounted } from 'features/utils/hook';
import {
  messageToClearVPText,
  VrsChatCollapseButton,
  VrsChatMessageLocal,
  VrsChatMessageRemote,
} from 'features/vrs-chat/components';
import { VrsChatEventBus } from 'features/vrs-chat/services';
import {
  isShowPopupSelector,
  isVrsChatCollapsedSelector,
  openVrsChatPopup,
  toggleVrsChatPopup,
} from 'features/vrs-chat/store';

export interface VrsChatHandle {
  scrollToChat: () => void;
}

export const VrsChat = forwardRef<VrsChatHandle>(() => {
  const isShowPopup = useAppSelector(isShowPopupSelector);
  const isCollapsed = useAppSelector(isVrsChatCollapsedSelector);
  const localAgentNumber = useAppSelector(agentNumberSelector);
  const remoteUserNumber = useAppSelector(deafNameSelector);
  const dispatch = useAppDispatch();
  const whileComponentMounted = useWhileComponentMounted();
  const [isWaitingClearConfirm, setIsWaitingClearConfirm] = useState(false);

  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);

  const closeChat = () => {
    dispatch(toggleVrsChatPopup());
  };

  const closeConfirmHandler = () => {
    closeChat();
    setIsWaitingClearConfirm(false);
  };

  const clearChat = () => {
    setIsWaitingClearConfirm(true);
    SipSessionEventBus.outgoingInfo$.next(messageToClearVPText);
    VrsChatEventBus.$clearMessage.next();
  };
  const handleReceiveMessage = (message: string) => {
    if (message.trim() && !isShowPopup) {
      dispatch(openVrsChatPopup());
    }
  };

  useEffect(() => {
    const receiveMessageSubscription = VrsChatEventBus.$receiveMessage
      .pipe(whileComponentMounted())
      .subscribe(handleReceiveMessage);

    return () => {
      receiveMessageSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleReceiveMessage]);

  return (
    <Container $isShowPopup={isShowPopup} ref={containerRef}>
      <Header>
        <TitleWithIcon>
          <HeaderTitle>{t('shareText')}</HeaderTitle>
        </TitleWithIcon>
        <VrsChatCollapseButton />
      </Header>

      <CollapsibleContent
        $isCollapsed={isCollapsed}
        id='vrs-chat-collapsible-content'
        data-testid='vrs-chat-collapsible-content'
      >
        <Separator />
        <ChatHistory>
          <LocalMessage>
            <VrsChatMessageLocal agentNumber={localAgentNumber} />
          </LocalMessage>
          <RemoteMessage>
            <VrsChatMessageRemote
              isWaitingClearConfirm={isWaitingClearConfirm}
              userName={remoteUserNumber}
              onCloseConfirm={closeConfirmHandler}
            />
          </RemoteMessage>
        </ChatHistory>
        <ClearButton
          loading={isWaitingClearConfirm}
          variant='outlined'
          color='secondary'
          onClick={clearChat}
          hotkey={Hotkey.ClearText}
          title='clearAndClose'
        >
          {t('clearAndClose')}
        </ClearButton>
      </CollapsibleContent>
    </Container>
  );
});

// Add displayName for component identification
VrsChat.displayName = 'VrsChat';

const Container = styledProps('div')<{
  $isShowPopup: boolean;
}>`
  width: 100%;
  border-radius: 8px;
  background-color: ${theme.palette.grey[900]};
    padding: ${theme.spacing(14, 0, 12)};
  ${({ $isShowPopup }) =>
    !$isShowPopup &&
    `
  display: none;
  `};
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled('div')`
  ${typography(theme.typography.body1)};
  color: ${theme.palette.common.white};
`;

const TitleWithIcon = styled('div')`
  align-items: center;
  display: flex;
  padding: ${theme.spacing(0, 12, 0)};
`;

const ChatHistory = styled('div')`
  margin-bottom: 4px;
`;

const RemoteMessage = styled('div')`
  margin: ${theme.spacing(14, 2, 16, 12)};
`;

const LocalMessage = styled('div')`
  margin: ${theme.spacing(14, 2, 16, 12)};
`;

const CollapsibleContent = styledProps('div')<{
  $isCollapsed: boolean;
}>`
  max-height: ${({ $isCollapsed }) => ($isCollapsed ? '0px' : '1000px')};
  overflow: hidden;
`;

const ClearButton = styled(Button)`
  margin: ${theme.spacing(8, 12, 0, 12)};
  width: calc(100% - ${theme.spacing(24)});
`;
